import type {
  SavingsTransaction,
  SavingsTransactionReason
} from '@/interfaces/SavingsTransaction.interface';
import { filenameFromContentDisposition } from '../savings-products/response-helpers';
import BaseApiClient from '../BaseApiClient';
import {
  SAVINGS_API_VERSION_HEADERS,
  toSavingsListResponse
} from '../savings-reference-resource';
import type {
  ListDataParams,
  ListDataResponse,
  ListExportParams
} from '../types';

/** The reason vocabulary is six curated values, so one page holds all of it. */
const REASONS_PAGE = { page: 1, perPage: 100 };

export class ApiSavingsTransactions extends BaseApiClient {
  async list(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<SavingsTransaction>> {
    const search = searchTerm?.trim();
    const res = await this.client.getList(
      '/savings-transactions',
      {
        ...listData,
        filters: { ...listData.filters, ...(search ? { search } : {}) }
      },
      { headers: SAVINGS_API_VERSION_HEADERS }
    );
    return toSavingsListResponse<SavingsTransaction>(res.data);
  }

  async export(
    listData: ListExportParams,
    searchTerm?: string
  ): Promise<{ blob: Blob; filename: string }> {
    const search = searchTerm?.trim();
    const response = await this.client.getList(
      '/savings-transactions/export',
      {
        filters: { ...listData.filters, ...(search ? { search } : {}) },
        sort: listData.sort
      },
      {
        responseType: 'blob',
        headers: { ...SAVINGS_API_VERSION_HEADERS, Accept: 'text/csv' }
      }
    );

    return {
      blob: new Blob([response.data], { type: 'text/csv;charset=utf-8' }),
      filename: filenameFromContentDisposition(
        response.headers['content-disposition'],
        'savings-transactions.csv'
      )
    };
  }

  async listReasons(): Promise<SavingsTransactionReason[]> {
    const res = await this.client.getList(
      '/savings-transaction-reasons',
      REASONS_PAGE,
      { headers: SAVINGS_API_VERSION_HEADERS }
    );
    return res.data.data;
  }
}

export { savingsTransactionsQueryKeys } from './query-keys';
export { useSavingsAccountRecentTransactionsQuery } from './use-savings-transactions-queries';
export { useSavingsTransactionsExportMutation } from './use-savings-transactions-mutations';
